import React, { useState } from "react"
import _ from "lodash"
import I18n from "@helper/i18nInstance"

import "./QuizSandbox.scss"
import Mocks, { getMockForSubpage, IMockState } from "./Mocks"

import { Section, Block, Note } from "../../Sandbox"
import { QuizRaw } from ".."
import { WithQuiz } from "../context"
import { WithLayout } from "@Quiz/layout"
import { WithTeasers } from "@Quiz/Teaser"
import { WithRanking } from "@Quiz/Ranking"

import teaserDemoImageUrl from "./teaserDemoImage.png"
import QuizEmbedInfoContext, { EMBED_INFO_CONTEXT_MAGAZIN, EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE, EmbedHost, IQuizEmbedInfoContext } from "@Quiz/QuizEmbedInfoContext"
import PickOne from "../../Sandbox/PickOne"

export interface IQuizSandboxCompState {
  quiz?: Quiz.Api.Quiz
  busy: boolean
  lastSent?: [string, string, Date]
  mobileWidth: number
}

const OVERRIDE_COLOR_SCHEME: Quiz.Api.QuizColorTheme = {
  cardBorderSizeMobile: {light: "1px", dark: "1px"},
  cardBorderSizeDesktop: {light: "10px", dark: "10px"},
  cardBg: { dark: "#555588", light: "#bbbbff"},
  cardBgIntense: { dark: "#333388", light: "#aaaaff"},
  cardBgSubtle: { dark: "#333388", light: "#aaaaff"},
  cardBorder: { dark: "#000088", light: "#3333ff"},
  roundNumberBg: { dark: "#000088", light: "#3333ff"},
  roundNumberProgress: { dark: "#222288", light: "#6666ff"},
}

const ChangeColorPallette: React.FC<{setPalletteId(id: string): void, palettes?: IQuizEmbedInfoContext["colorPalettes"], current: string}> = ({setPalletteId, current, palettes}) => {
  const entries = _.map(_.uniq(palettes
    ? [ ...palettes.cover, ...palettes.question, ...palettes.result ]
    : [
      ...EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE.colorPalettes.cover,
      ...EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE.colorPalettes.question,
      ...EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE.colorPalettes.result,
      ...EMBED_INFO_CONTEXT_MAGAZIN.colorPalettes.cover,
      ...EMBED_INFO_CONTEXT_MAGAZIN.colorPalettes.question,
      ...EMBED_INFO_CONTEXT_MAGAZIN.colorPalettes.result,
    ]))
  return <PickOne
    current={current}
    items={entries.map((e) => ({ val: e, key: e }))}
    change={(val) => setPalletteId(val)}
    label="Color pallette" />
}

const MOBILE_WIDTHS = [320, 360, 380]

const SAMPLE_TEASERS: Teaser.Api.Teaser[] = [
  {
    image: {"1x": teaserDemoImageUrl},
    title: "Hauptpreis",
    headline: "Ein super Pilzkorb!",
    text: "Unter den top Quizzern verlosen wir diesen schönen Korb",
    link_url: "#link-url",
    link_text: "Sieh mehr!"
  },
  {
    image: {"1x": teaserDemoImageUrl},
    text: "Hier steht nur ein Text neben, kein Link und keine Überschrift",
  }
]

const SAMPLE_RANKING: Ranking.Api.GetRankingResp = {
  quiz: {
    title: "Superquiz",
    own_entry: {rank: 200, nickname: "Superplayer", score: 20, own: true},
    entries: [
      {rank: 1, nickname: "Sonic the Hedgehog", score: 125000},
      {rank: 2, nickname: "Samus Aran", score: 120000},
      {rank: 3, nickname: "Princess Peach", score: 118000},
      {rank: 4, nickname: "She-Ra", score: 105000},
      {rank: 5, nickname: "Mario Mario", score: 100000}
    ],
    current_page: 1,
    total_pages: 3
  },
  campaign: {
    title: "SZ Adventskalender 2020",
    own_entry: {rank: 3, nickname: "Superplayer", own: true},
    entries: [
      {rank: 1, nickname: "Sonic the Hedgehog"},
      {rank: 2, nickname: "Samus Aran"},
      {rank: 3, nickname: "Superplayer", own: true},
      {rank: 4, nickname: "She-Ra"},
      {rank: 5, nickname: "Mario Mario"}
    ],
    current_page: 2,
    total_pages: 2
  }
}

const QuizSandbox: React.FC<{ subPage?: number, setSubPage(subpage: number): void }> = ({ subPage, setSubPage }) => {
  const [ overrideDarkLight, setOverrideDarkLight ] = useState<"dark" | "light" | undefined>("light")
  const [ state, setState ] = useState<IQuizSandboxCompState>(() => ({
    quiz: typeof(subPage) === "number" ? getMockForSubpage(subPage)?.quiz : undefined,
    busy: false,
    mobileWidth: 360
  }))
  const [ overrideColorTheme, setOverrideColorScheme ] = useState(false)
  const [ withTeasers, setWithTeasers ] = useState(true)
  const [ withRanking, setWithRanking ] = useState(true)
  const [ embedHost, __setEmbedHost ] = useState<EmbedHost>("sz-base")
  const embedInfoCont: IQuizEmbedInfoContext = embedHost === "sz-base"
    ? { ...EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE }
    : { ...EMBED_INFO_CONTEXT_MAGAZIN }
  const [ palletteId, setPalletteId ] = useState(embedInfoCont.colorPalettes.question[0])

  const setEmbedHost = (host: EmbedHost) => {
    setPalletteId((host === "sz-base" ? EMBED_INFO_CONTEXT_SZ_ON_SEPARATE_PAGE : EMBED_INFO_CONTEXT_MAGAZIN).colorPalettes.question[0])
    __setEmbedHost(host)
  }

  const teasers = withTeasers ? SAMPLE_TEASERS : []
  const ranking = withRanking ? SAMPLE_RANKING : {}

  const setBusyAndLog = (msg: string) => {
    return (...args: any[]) => {
      setState((prev) => {
        return {
          ...prev,
          busy: true,
          lastSent: [msg, JSON.stringify(args), new Date()]
        }
      })
    }
  }

  const answerQuestion = setBusyAndLog("answerQuestion")
  const startQuiz = setBusyAndLog("startQuiz")
  const cancelQuiz = setBusyAndLog("cancelQuiz")
  const continueQuiz = setBusyAndLog("continueQuiz")
  const startQuestionCountdown = setBusyAndLog("startQuestionCountdown")
  const enterRanking = setBusyAndLog("startQuestionCountdown")

  return <>
    <Section title="Quiz">
      <QuizEmbedInfoContext.Provider value={embedInfoCont}>
        <WithRanking ranking={ranking} toPage={(p) => console.log("toPage", p)}>
          <WithTeasers teasers={teasers}>
            <WithQuiz quiz={state.quiz} busy={state.busy} actions={{startQuiz, answerQuestion, cancelQuiz, continueQuiz, startQuestionCountdown, enterRanking}}>
              <Block className="neo__quizSandbox__mocksAndMobileHolder">
                <div className="neo__quizSandbox__mocksHolder">
                  {state.lastSent
                    ? <div className="neo__quizSandbox__lastSent" key={state.lastSent[2].getTime()}>
                        <em>{I18n.strftime(state.lastSent[2], "%H:%M:%S")}-{state.lastSent[2].getMilliseconds()}:</em>{" "}<span>{state.lastSent[0]}</span><span>{state.lastSent[1]}</span></div>
                    : <div className="neo__quizSandbox__lastSent" key={"x"}>
                        <em>(last sent message to server)</em></div>}
                    <PickOne
                      label="dark/light?"
                      current={overrideDarkLight}
                      items={[{val: "dark", key: "dark"}, {val: "light", key: "light"}, {val: undefined, key: "browser-default"}]}
                      change={(newVal) => setOverrideDarkLight(newVal as any)} />
                    <PickOne
                      label="Embed host"
                      current={embedHost}
                      items={[{val: "sz-base", key: "SZ"}, {val: "magazin", key: "Magazin"}]}
                      change={(val) => setEmbedHost(val as any)} />
                    <ChangeColorPallette setPalletteId={setPalletteId} current={palletteId} palettes={embedInfoCont.colorPalettes} />
                    <PickOne
                      label="override color pallette?"
                      current={overrideColorTheme}
                      items={[{val: false, key: "-"}, {val: true, key: "override with fixed scheme"}]}
                      change={(val) => setOverrideColorScheme(val)} />
                    <PickOne
                      label="Show teasers below quiz?"
                      current={withTeasers}
                      items={[{val: false, key: "-"}, {val: true, key: "showTeasers"}]}
                      change={(val) => setWithTeasers(val)} />
                    <PickOne
                      label="Show ranking (only on result screen)?"
                      current={withRanking}
                      items={[{val: false, key: "-"}, {val: true, key: "showRanking"}]}
                      change={(val) => setWithRanking(val)} />
                  <Mocks
                    setState={(st: IMockState, mockSubpageIndex: number) => {setState({...state, ...st}); setSubPage(mockSubpageIndex)}} />
                </div>
                <div className="neo__quizSandbox__mobileHolder" style={{width: `${state.mobileWidth}px`}}>
                  <div>
                    <h3 className="sandbox__selectMobileWidthHolder">
                      {_.map(MOBILE_WIDTHS, (w) => {
                        return (
                          <a
                            key={w}
                            className={`sandbox__selectMobileWidth ${state.mobileWidth === w ? "--active" : ""}`}
                            onClick={() => setState({...state, mobileWidth: w})}>
                            {w}px
                          </a>)
                      })}
                    </h3>
                  </div>
                  <WithLayout embedHost={embedHost} overrideDesktopOrMobile={"mobile"} palletteId={palletteId} forceMode={overrideDarkLight} overrideColorTheme={overrideColorTheme ? OVERRIDE_COLOR_SCHEME : undefined}>
                    <QuizRaw />
                  </WithLayout>
                </div>
              </Block>
              <Block>
                <div style={{width: 640}}>
                  <Note>Magazin only has 640px width 😱</Note>
                  <WithLayout embedHost={embedHost} overrideDesktopOrMobile={"desktop"} palletteId={palletteId} forceMode={overrideDarkLight} overrideColorTheme={overrideColorTheme ? OVERRIDE_COLOR_SCHEME : undefined}>
                    <QuizRaw />
                  </WithLayout>
                </div>
              </Block>
              <Block>
                <WithLayout embedHost={embedHost} overrideDesktopOrMobile={"desktop"} palletteId={palletteId} forceMode={overrideDarkLight} overrideColorTheme={overrideColorTheme ? OVERRIDE_COLOR_SCHEME : undefined}>
                  <QuizRaw />
                </WithLayout>
              </Block>
            </WithQuiz>
          </WithTeasers>
        </WithRanking>
      </QuizEmbedInfoContext.Provider>
    </Section>
  </>
}

export default QuizSandbox
